import { useCallback, useEffect, useTransition } from "react";
import { useRecoilValue } from "recoil";

import { useRideStationActions } from "@ryde/redux";
import {
  BookingFormFields,
  DirectionEnum,
  dropdownPassengersWithStationsAtom,
  dropdownStationListAtom,
  filterHomeStationsCategory,
  filterWorkStationsCategory,
  includeSelectedStation,
  stationsForHomeSelector,
  stationsForWorkSelector,
  type BookingDto,
  type IDropdownItemStations,
  type IPassengerForStationDto,
  type RideStationCategory,
} from "@ryde/shared";

import type {
  IDropdownCustomTransformationsFn,
  IDropdownCustomTransformationsFnResponse,
  IDropdownCustomTransformationsProps,
} from "../models";

export const useBookingStationOptionCustomTransformerFn = (): {
  bookingStationOptionCustomTransformerFn: IDropdownCustomTransformationsFn;
} => {
  const [isDataLoading, startTransition] = useTransition();
  const stationsForHomeCategories = useRecoilValue(stationsForHomeSelector);
  const stationsForWorkCategories = useRecoilValue(stationsForWorkSelector);
  const stations = useRecoilValue(dropdownStationListAtom);
  const passengersDropdownList = useRecoilValue(
    dropdownPassengersWithStationsAtom,
  );

  const { fetchDropdownPassengersAndStations } = useRideStationActions();

  useEffect(() => {
    if (passengersDropdownList?.length) {
      return;
    }

    startTransition(() => {
      fetchDropdownPassengersAndStations();
    });
  }, [fetchDropdownPassengersAndStations, passengersDropdownList?.length]);

  const getStationsFromDropdownList = (
    isPickUp: boolean,
    passengers: IPassengerForStationDto[],
    stationCategories: RideStationCategory[],
    stationsDropdownList: IDropdownItemStations[],
  ): IDropdownItemStations[] => {
    if (isPickUp) {
      return filterHomeStationsCategory({
        passengers,
        stationCategories,
        stationsDropdownList,
      });
    } else {
      return filterWorkStationsCategory({
        passengers,
        stationCategories,
        stationsDropdownList,
      });
    }
  };

  const getStationsToDropdownList = (
    isPickUp: boolean,
    passengers: IPassengerForStationDto[],
    stationCategories: RideStationCategory[],
    stationsDropdownList: IDropdownItemStations[],
  ): IDropdownItemStations[] => {
    if (isPickUp) {
      return filterWorkStationsCategory({
        passengers,
        stationCategories,
        stationsDropdownList,
      });
    } else {
      return filterHomeStationsCategory({
        passengers,
        stationCategories,
        stationsDropdownList,
      });
    }
  };

  const bookingStationOptionCustomTransformerFn = useCallback(
    ({
      controlName,
      formValues,
      dropdownItems,
    }: IDropdownCustomTransformationsProps): IDropdownCustomTransformationsFnResponse => {
      const { direction, passengerId } = (formValues as BookingDto) ?? {};

      if (
        !isDataLoading &&
        direction &&
        passengersDropdownList?.length &&
        stations?.length
      ) {
        const isPickUp = direction === DirectionEnum.PickUp;
        const passenger =
          passengersDropdownList?.filter(x => x.id === passengerId) ?? [];

        const withSelectedStation = (
          filteredStations: IDropdownItemStations[],
        ): IDropdownItemStations[] =>
          includeSelectedStation({
            filteredStations,
            selectedStationId: formValues?.[controlName],
            // The field's own list also holds a station created after the shared list was loaded.
            allStations: dropdownItems
              ? [...stations, ...dropdownItems]
              : stations,
          });

        if (controlName === BookingFormFields.originStationId) {
          const stationsCategories = isPickUp
            ? stationsForHomeCategories
            : stationsForWorkCategories;

          return withSelectedStation(
            getStationsFromDropdownList(
              isPickUp,
              passenger,
              stationsCategories as RideStationCategory[],
              stations,
            ),
          );
        }

        if (controlName === BookingFormFields.destinationStationId) {
          const stationsCategories = isPickUp
            ? stationsForWorkCategories
            : stationsForHomeCategories;

          return withSelectedStation(
            getStationsToDropdownList(
              isPickUp,
              passenger,
              stationsCategories as RideStationCategory[],
              stations,
            ),
          );
        }
      }

      return undefined;
    },
    [
      isDataLoading,
      passengersDropdownList,
      stations,
      stationsForHomeCategories,
      stationsForWorkCategories,
    ],
  );

  return { bookingStationOptionCustomTransformerFn };
};
